import React, {FormEvent} from 'react';
import HomeModalShell from '../../home/components/HomeModalShell';
import {ProfileTab} from '../constants';
import styles from '../Profile.module.css';

export type ProfileServiceEditorMode = 'create' | 'edit';

export interface ProfileAthleticFormValues {
  sportName: string;
  skillLevel: string;
  yearsOfExperience: string;
  certifications: string;
  availability: string;
}

export interface ProfileArtisticFormValues {
  artForm: string;
  proficiencyLevel: string;
  yearsOfPractice: string;
  portfolioLinks: string;
  serviceRates: string;
}

export interface ProfileGeneralServiceFormValues {
  title: string;
  description: string;
  pricing: string;
  availability: string;
  contactPreferences: string;
}

export interface ProfileCollegeServiceFormValues {
  opportunityType: string;
  title: string;
  description: string;
  eligibility: string;
  deadline: string;
  learnMoreUrl: string;
}

interface ProfileServiceEditorModalProps {
  isOpen: boolean;
  tab: ProfileTab;
  mode: ProfileServiceEditorMode;
  loading: boolean;
  error: string | null;
  validationError: string | null;
  athleticValues: ProfileAthleticFormValues;
  artisticValues: ProfileArtisticFormValues;
  generalServiceValues: ProfileGeneralServiceFormValues;
  collegeServiceValues: ProfileCollegeServiceFormValues;
  onClose: () => void;
  onAthleticChange: (field: keyof ProfileAthleticFormValues, value: string) => void;
  onArtisticChange: (field: keyof ProfileArtisticFormValues, value: string) => void;
  onGeneralServiceChange: (field: keyof ProfileGeneralServiceFormValues, value: string) => void;
  onCollegeServiceChange: (field: keyof ProfileCollegeServiceFormValues, value: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}

const getModalCopy = (tab: ProfileTab, mode: ProfileServiceEditorMode) => {
  const action = mode === 'create' ? 'Add' : 'Edit';

  switch (tab) {
    case 'athletic':
      return {
        title: `${action} Athletic Experience`,
        description: 'Manage the athletic services and experience details displayed on your profile.',
        submitLabel: mode === 'create' ? 'Add Athletic Experience' : 'Save Athletic Changes',
      };
    case 'artistic':
      return {
        title: `${action} Artistic Experience`,
        description: 'Manage the artistic experience details and portfolio links displayed on your profile.',
        submitLabel: mode === 'create' ? 'Add Artistic Experience' : 'Save Artistic Changes',
      };
    case 'services':
      return {
        title: `${action} Service Listing`,
        description: 'Manage the service listing information displayed on your profile.',
        submitLabel: mode === 'create' ? 'Add Service Listing' : 'Save Service Changes',
      };
    case 'college':
      return {
        title: `${action} College/University Opportunity`,
        description: 'Share scholarships, recruiting, admissions, and other college opportunities for students.',
        submitLabel: mode === 'create' ? 'Add College Opportunity' : 'Save College Changes',
      };
    default:
      return {
        title: `${action} Item`,
        description: 'Manage your profile listing.',
        submitLabel: mode === 'create' ? 'Add Item' : 'Save Changes',
      };
  }
};

const ProfileServiceEditorModal: React.FC<ProfileServiceEditorModalProps> = ({
  isOpen,
  tab,
  mode,
  loading,
  error,
  validationError,
  athleticValues,
  artisticValues,
  generalServiceValues,
  collegeServiceValues,
  onClose,
  onAthleticChange,
  onArtisticChange,
  onGeneralServiceChange,
  onCollegeServiceChange,
  onSubmit,
}) => {
  if (!isOpen) {
    return null;
  }

  const modalCopy = getModalCopy(tab, mode);

  return (
    <HomeModalShell
      backdropClassName={styles.editorModalBackdrop}
      panelClassName={`${styles.editorModalCard} ${styles.serviceEditorModalCard}`}
      ariaLabelledBy="profile-service-editor-title"
      ariaDescribedBy="profile-service-editor-description"
      onClose={onClose}
      closeDisabled={loading}
    >
      <div className={styles.editorModalHeader}>
        <div>
          <h2 id="profile-service-editor-title" className={styles.editorModalTitle}>
            {modalCopy.title}
          </h2>
          <p id="profile-service-editor-description" className={styles.editorModalDescription}>
            {modalCopy.description}
          </p>
        </div>
        <button
          type="button"
          className={styles.editorModalClose}
          onClick={onClose}
          disabled={loading}
          aria-label="Close profile service editor"
        >
          ×
        </button>
      </div>

      <form onSubmit={onSubmit} className={styles.editorModalForm}>
        <div className={styles.editorModalGrid}>
          {tab === 'athletic' ? (
            <>
              <label className={styles.editorField}>
                <span className={styles.editorFieldLabel}>Sport Name</span>
                <input
                  className={styles.editorInput}
                  type="text"
                  value={athleticValues.sportName}
                  onChange={(event) => onAthleticChange('sportName', event.target.value)}
                  placeholder="Basketball"
                  disabled={loading}
                />
              </label>

              <label className={styles.editorField}>
                <span className={styles.editorFieldLabel}>Skill Level</span>
                <input
                  className={styles.editorInput}
                  type="text"
                  value={athleticValues.skillLevel}
                  onChange={(event) => onAthleticChange('skillLevel', event.target.value)}
                  placeholder="Professional"
                  disabled={loading}
                />
              </label>

              <label className={styles.editorField}>
                <span className={styles.editorFieldLabel}>Years of Experience</span>
                <input
                  className={styles.editorInput}
                  type="number"
                  min="0"
                  step="1"
                  value={athleticValues.yearsOfExperience}
                  onChange={(event) => onAthleticChange('yearsOfExperience', event.target.value)}
                  placeholder="5"
                  disabled={loading}
                />
              </label>

              <label className={styles.editorField}>
                <span className={styles.editorFieldLabel}>Availability</span>
                <input
                  className={styles.editorInput}
                  type="text"
                  value={athleticValues.availability}
                  onChange={(event) => onAthleticChange('availability', event.target.value)}
                  placeholder="Mon-Fri 6 AM - 6 PM"
                  disabled={loading}
                />
              </label>

              <label className={`${styles.editorField} ${styles.editorFieldWide}`}>
                <span className={styles.editorFieldLabel}>Certifications</span>
                <textarea
                  className={styles.editorTextarea}
                  value={athleticValues.certifications}
                  onChange={(event) => onAthleticChange('certifications', event.target.value)}
                  placeholder={'One certification per line\nNSCA Certified\nCPR Certified'}
                  rows={5}
                  disabled={loading}
                />
                <span className={styles.editorHint}>Enter one certification per line.</span>
              </label>
            </>
          ) : null}

          {tab === 'artistic' ? (
            <>
              <label className={styles.editorField}>
                <span className={styles.editorFieldLabel}>Art Form</span>
                <input
                  className={styles.editorInput}
                  type="text"
                  value={artisticValues.artForm}
                  onChange={(event) => onArtisticChange('artForm', event.target.value)}
                  placeholder="Photography"
                  disabled={loading}
                />
              </label>

              <label className={styles.editorField}>
                <span className={styles.editorFieldLabel}>Proficiency Level</span>
                <input
                  className={styles.editorInput}
                  type="text"
                  value={artisticValues.proficiencyLevel}
                  onChange={(event) => onArtisticChange('proficiencyLevel', event.target.value)}
                  placeholder="Advanced"
                  disabled={loading}
                />
              </label>

              <label className={styles.editorField}>
                <span className={styles.editorFieldLabel}>Years of Practice</span>
                <input
                  className={styles.editorInput}
                  type="number"
                  min="0"
                  step="1"
                  value={artisticValues.yearsOfPractice}
                  onChange={(event) => onArtisticChange('yearsOfPractice', event.target.value)}
                  placeholder="8"
                  disabled={loading}
                />
              </label>

              <label className={styles.editorField}>
                <span className={styles.editorFieldLabel}>Service Rates</span>
                <input
                  className={styles.editorInput}
                  type="text"
                  value={artisticValues.serviceRates}
                  onChange={(event) => onArtisticChange('serviceRates', event.target.value)}
                  placeholder="$150/session"
                  disabled={loading}
                />
              </label>

              <label className={`${styles.editorField} ${styles.editorFieldWide}`}>
                <span className={styles.editorFieldLabel}>Portfolio Links</span>
                <textarea
                  className={styles.editorTextarea}
                  value={artisticValues.portfolioLinks}
                  onChange={(event) => onArtisticChange('portfolioLinks', event.target.value)}
                  placeholder={'One portfolio URL per line\nhttps://portfolio.example.com\nhttps://instagram.com/artist'}
                  rows={5}
                  disabled={loading}
                />
                <span className={styles.editorHint}>Enter one portfolio link per line.</span>
              </label>
            </>
          ) : null}

          {tab === 'services' ? (
            <>
              <label className={styles.editorField}>
                <span className={styles.editorFieldLabel}>Title</span>
                <input
                  className={styles.editorInput}
                  type="text"
                  value={generalServiceValues.title}
                  onChange={(event) => onGeneralServiceChange('title', event.target.value)}
                  placeholder="Performance Coaching"
                  disabled={loading}
                />
              </label>

              <label className={styles.editorField}>
                <span className={styles.editorFieldLabel}>Pricing</span>
                <input
                  className={styles.editorInput}
                  type="text"
                  value={generalServiceValues.pricing}
                  onChange={(event) => onGeneralServiceChange('pricing', event.target.value)}
                  placeholder="$120/session"
                  disabled={loading}
                />
              </label>

              <label className={styles.editorField}>
                <span className={styles.editorFieldLabel}>Availability</span>
                <input
                  className={styles.editorInput}
                  type="text"
                  value={generalServiceValues.availability}
                  onChange={(event) => onGeneralServiceChange('availability', event.target.value)}
                  placeholder="Weekends and evenings"
                  disabled={loading}
                />
              </label>

              <label className={styles.editorField}>
                <span className={styles.editorFieldLabel}>Contact Preferences</span>
                <input
                  className={styles.editorInput}
                  type="text"
                  value={generalServiceValues.contactPreferences}
                  onChange={(event) => onGeneralServiceChange('contactPreferences', event.target.value)}
                  placeholder="Email or direct message"
                  disabled={loading}
                />
              </label>

              <label className={`${styles.editorField} ${styles.editorFieldWide}`}>
                <span className={styles.editorFieldLabel}>Description</span>
                <textarea
                  className={styles.editorTextarea}
                  value={generalServiceValues.description}
                  onChange={(event) => onGeneralServiceChange('description', event.target.value)}
                  placeholder="Describe your service, what is included, and who it is for."
                  rows={5}
                  disabled={loading}
                />
              </label>
            </>
          ) : null}

          {tab === 'college' ? (
            <>
              <label className={styles.editorField}>
                <span className={styles.editorFieldLabel}>Opportunity Type</span>
                <select
                  className={styles.editorInput}
                  value={collegeServiceValues.opportunityType}
                  onChange={(event) => onCollegeServiceChange('opportunityType', event.target.value)}
                  disabled={loading}
                >
                  <option value="">Select one</option>
                  <option value="Scholarship">Scholarship</option>
                  <option value="Athletic Recruiting">Athletic Recruiting</option>
                  <option value="Admissions">Admissions</option>
                  <option value="Other">Other</option>
                </select>
              </label>

              <label className={styles.editorField}>
                <span className={styles.editorFieldLabel}>Title</span>
                <input
                  className={styles.editorInput}
                  type="text"
                  value={collegeServiceValues.title}
                  onChange={(event) => onCollegeServiceChange('title', event.target.value)}
                  placeholder="Womens Soccer Scholarship Review"
                  disabled={loading}
                />
              </label>

              <label className={styles.editorField}>
                <span className={styles.editorFieldLabel}>Deadline</span>
                <input
                  className={styles.editorInput}
                  type="date"
                  value={collegeServiceValues.deadline}
                  onChange={(event) => onCollegeServiceChange('deadline', event.target.value)}
                  disabled={loading}
                />
              </label>

              <label className={styles.editorField}>
                <span className={styles.editorFieldLabel}>Learn More URL</span>
                <input
                  className={styles.editorInput}
                  type="url"
                  value={collegeServiceValues.learnMoreUrl}
                  onChange={(event) => onCollegeServiceChange('learnMoreUrl', event.target.value)}
                  placeholder="https://university.example.edu/apply"
                  disabled={loading}
                />
              </label>

              <label className={`${styles.editorField} ${styles.editorFieldWide}`}>
                <span className={styles.editorFieldLabel}>Description</span>
                <textarea
                  className={styles.editorTextarea}
                  value={collegeServiceValues.description}
                  onChange={(event) => onCollegeServiceChange('description', event.target.value)}
                  placeholder="Explain the opportunity, what students should expect, and how to take the next step."
                  rows={5}
                  disabled={loading}
                />
              </label>

              <label className={`${styles.editorField} ${styles.editorFieldWide}`}>
                <span className={styles.editorFieldLabel}>Eligibility</span>
                <textarea
                  className={styles.editorTextarea}
                  value={collegeServiceValues.eligibility}
                  onChange={(event) => onCollegeServiceChange('eligibility', event.target.value)}
                  placeholder="List GPA targets, class year requirements, roster needs, or other criteria."
                  rows={4}
                  disabled={loading}
                />
              </label>
            </>
          ) : null}
        </div>

        {validationError ? (
          <div className={`${styles.editorMessage} ${styles.editorMessageValidation}`}>
            {validationError}
          </div>
        ) : null}

        {error ? (
          <div className={`${styles.editorMessage} ${styles.editorMessageError}`}>
            {error}
          </div>
        ) : null}

        <div className={styles.editorModalActions}>
          <button
            type="button"
            className={`${styles.editorModalButton} ${styles.editorModalButtonSecondary}`}
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={`${styles.editorModalButton} ${styles.editorModalButtonPrimary}`}
            disabled={loading}
          >
            {loading ? 'Saving...' : modalCopy.submitLabel}
          </button>
        </div>
      </form>
    </HomeModalShell>
  );
};

export default React.memo(ProfileServiceEditorModal);
